import { currencyInfo, minorFactor } from './currency.ts';

/**
 * An amount in integer minor units (pence, grosze) of one currency.
 * Never a JS float: `minor` is a bigint, so fractions of a penny cannot exist.
 */
export type Money = {
  readonly minor: bigint;
  readonly currency: string;
};

export class CurrencyMismatchError extends Error {
  constructor(a: string, b: string) {
    super(`Currency mismatch: ${a} vs ${b}`);
    this.name = 'CurrencyMismatchError';
  }
}

const INTEGER_STRING = /^-?\d+$/;

/** Parses a minor-unit value from bigint, a safe integer, or an integer string (JSON transport). */
export function toMinor(value: bigint | number | string): bigint {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) {
      throw new RangeError(`Minor units must be a safe integer, got ${value}`);
    }
    return BigInt(value);
  }
  if (!INTEGER_STRING.test(value)) {
    throw new RangeError(`Minor units must be an integer string, got "${value}"`);
  }
  return BigInt(value);
}

export function money(minor: bigint | number | string, currency: string): Money {
  currencyInfo(currency); // validates the code
  return Object.freeze({ minor: toMinor(minor), currency });
}

export function zero(currency: string): Money {
  return money(0n, currency);
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency);
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.minor + b.minor, a.currency);
}

export function negate(a: Money): Money {
  return money(-a.minor, a.currency);
}

export function absolute(a: Money): Money {
  return a.minor < 0n ? negate(a) : a;
}

export function sum(items: readonly Money[], currency: string): Money {
  let total = 0n;
  for (const item of items) {
    if (item.currency !== currency) throw new CurrencyMismatchError(currency, item.currency);
    total += item.minor;
  }
  return money(total, currency);
}

export function compare(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b);
  return a.minor < b.minor ? -1 : a.minor > b.minor ? 1 : 0;
}

/** Major units as a decimal string without locale formatting, e.g. "-19.99". */
export function toDecimalString(a: Money): string {
  const factor = minorFactor(a.currency);
  const scale = currencyInfo(a.currency).scale;
  const negative = a.minor < 0n;
  const absMinor = negative ? -a.minor : a.minor;
  const whole = (absMinor / factor).toString();
  const fraction = scale > 0 ? '.' + (absMinor % factor).toString().padStart(scale, '0') : '';
  return (negative ? '-' : '') + whole + fraction;
}

/**
 * Approximate major-unit value as a JS number. Only for chart geometry and similar
 * presentation — never for arithmetic on money.
 */
export function toApproximateNumber(a: Money): number {
  return Number(a.minor) / Number(minorFactor(a.currency));
}
